import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useSelector } from "react-redux";
import { Download, ScrollText } from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import DataTable from "../../components/dashboard/DataTable";
import ActivitySummaryCharts from "../../components/dashboard/ActivitySummaryCharts";
import { ActivityIcon, formatActivityRelative } from "../../components/dashboard/RecentActivity";
import Button from "../../components/ui/Button";
import { USE_MOCK_AUTH } from "../../config/api";
import {
  fetchProviderActivityLogs,
  fetchProviderActivityLogsSummary,
} from "../../services/activityLogsApi";
import { activityActorName, resolveActivityPath } from "../../utils/activityPath";
import { exportReport } from "../../utils/exportReport";
import { showError, showSuccess } from "../../utils/toast";

const EMPTY_FILTERS = {
  date_from: "",
  date_to: "",
};

function formatTimestamp(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export default function AuditTrailPage() {
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(useLiveApi);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exportFormat, setExportFormat] = useState("Excel");

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const loadSummary = useCallback(async () => {
    if (!useLiveApi) {
      setSummary(null);
      setSummaryLoading(false);
      return;
    }
    setSummaryLoading(true);
    try {
      setSummary(await fetchProviderActivityLogsSummary(token));
    } catch {
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [token, useLiveApi]);

  const load = useCallback(
    async (nextPage = 1) => {
      if (!useLiveApi) {
        setItems([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await fetchProviderActivityLogs(token, { ...applied, page: nextPage, limit: 25 });
        const nextItems = Array.isArray(data?.entries) ? data.entries : [];
        setItems(nextItems);
        setPage(Number(data?.pagination?.current_page || nextPage));
        setLastPage(Number(data?.pagination?.last_page || 1));
        setTotal(Number(data?.pagination?.total || 0));
      } catch (error) {
        showError(error.message || "Could not load audit trail.");
      } finally {
        setLoading(false);
      }
    },
    [applied, token, useLiveApi]
  );

  useEffect(() => {
    load(1);
    loadSummary();
  }, [load, loadSummary]);

  const applyFilters = (event) => {
    event?.preventDefault?.();
    setApplied({ ...filters });
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
  };

  const activeFilterCount = useMemo(
    () => Object.values(applied).filter((value) => value !== "").length,
    [applied]
  );

  const handleExport = () => {
    exportReport(
      exportFormat,
      {
        title: "Institution audit trail",
        type: "Audit Trail",
        period: applied.date_from || applied.date_to
          ? `${applied.date_from || "…"} to ${applied.date_to || "…"}`
          : "Account activity",
        summary,
        entries: items.map((item) => ({
          timestamp: item.created_at,
          who: activityActorName(item),
          event: item.title,
          event_type: item.event_type,
          message: item.message,
          ip: item.ip || item.data?.ip || "",
        })),
      },
      "institution-audit-trail"
    );
    showSuccess(`Exported as ${exportFormat}.`);
  };

  return (
    <PageShell
      title="Audit Trail"
      description="Sign-in and sign-out attempts, payments received, portfolio uploads, and profile updates for your institution."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value)}
            className="rounded-lg border border-primary/10 bg-white px-2.5 py-1.5 text-xs font-semibold text-primary"
          >
            {["Excel", "CSV", "Text", "PDF"].map((fmt) => (
              <option key={fmt} value={fmt}>
                {fmt}
              </option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={loading && !items.length}>
            <Download className="h-3.5 w-3.5" />
            Export page
          </Button>
        </div>
      }
    >
      <form
        onSubmit={applyFilters}
        className="mb-4 rounded-xl border border-primary/[0.07] bg-white p-4 shadow-[0_1px_2px_rgba(127,29,29,0.04)]"
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:max-w-xl">
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
              From
            </span>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilter("date_from", e.target.value)}
              className="w-full rounded-lg border border-primary/10 bg-white px-2.5 py-2 text-xs text-primary"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
              To
            </span>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilter("date_to", e.target.value)}
              className="w-full rounded-lg border border-primary/10 bg-white px-2.5 py-2 text-xs text-primary"
            />
          </label>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button type="submit" size="sm">
            Apply dates
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={resetFilters}>
            Reset
          </Button>
          {activeFilterCount > 0 ? (
            <span className="text-[11px] font-semibold text-primary/45">
              {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} applied
            </span>
          ) : null}
        </div>
      </form>

      <ActivitySummaryCharts
        summary={summary}
        loading={summaryLoading}
        volumeTitle="Account activity (30 days)"
        volumeCaption={`${summary?.total ?? 0} sign-ins, payments received, uploads, and profile updates`}
        eventTitle="By account event"
        actorsTitle="Who appeared"
      />

      <div className="mb-3 flex flex-wrap items-center gap-3 text-sm text-primary/50">
        <span className="inline-flex items-center gap-2">
          <ScrollText className="h-4 w-4" />
          <span>{loading ? "Loading…" : `${total.toLocaleString()} recorded action${total === 1 ? "" : "s"}`}</span>
        </span>
      </div>

      <div className="overflow-hidden rounded-xl border border-primary/[0.07] bg-white">
        {loading && items.length === 0 ? (
          <p className="px-5 py-16 text-center text-sm text-primary/45">Loading audit trail…</p>
        ) : items.length === 0 ? (
          <p className="px-5 py-16 text-center text-sm text-primary/45">
            No sign-ins, payments received, uploads, or profile updates in this range.
          </p>
        ) : (
          <DataTable
            embedded
            paginate={false}
            compact
            data={items}
            getRowId={(item) => item.activity_slug || item.id}
            columns={[
              {
                key: "when",
                header: "When",
                render: (item) => (
                  <div>
                    <p className="text-xs font-semibold tabular-nums text-primary">{formatTimestamp(item.created_at)}</p>
                    <p className="text-[10px] text-primary/35">{formatActivityRelative(item.created_at)}</p>
                  </div>
                ),
              },
              {
                key: "who",
                header: "Who",
                type: "name",
                render: (item) => (
                  <div>
                    <p className="text-xs font-semibold text-primary">{activityActorName(item)}</p>
                    {item.data?.email ? <p className="text-[10px] text-primary/40">{item.data.email}</p> : null}
                  </div>
                ),
              },
              {
                key: "event",
                header: "Event",
                render: (item) => (
                  <div className="flex items-start gap-2">
                    <ActivityIcon icon={item.icon} color={item.color} className="h-8 w-8" />
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-primary">{item.title}</p>
                      <p className="text-[10px] text-primary/40">{item.module_label || item.module}</p>
                    </div>
                  </div>
                ),
              },
              {
                key: "message",
                header: "Details",
                render: (item) => {
                  const path = resolveActivityPath(item, { portal: "provider" });
                  return (
                    <div>
                      <p className="text-xs leading-relaxed text-primary/65">{item.message}</p>
                      {item.ip || item.data?.ip ? (
                        <p className="mt-1 font-mono text-[10px] text-primary/35">IP {item.ip || item.data.ip}</p>
                      ) : null}
                      {path ? (
                        <Link to={path} className="mt-1.5 inline-flex text-[11px] font-semibold text-collect hover:underline">
                          Open related record
                        </Link>
                      ) : null}
                    </div>
                  );
                },
              },
            ]}
          />
        )}
      </div>

      {lastPage > 1 ? (
        <div className="mt-4 flex items-center justify-between">
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => load(page - 1)}>
            Previous
          </Button>
          <span className="text-xs text-primary/45">
            Page {page} of {lastPage}
          </span>
          <Button variant="outline" size="sm" disabled={page >= lastPage || loading} onClick={() => load(page + 1)}>
            Next
          </Button>
        </div>
      ) : null}
    </PageShell>
  );
}
